import { memo, useEffect } from 'react';
import { z } from 'zod';
import { createAuthentication } from '../../../../react-commons/query/createAuthentication';
import { createURL } from '../../../../react-commons/query/createURL';
import { when } from '../../../../react-commons/utils/when';
import { API_ROOT } from '../../resource/resource-base/APIVersion1';
import { IdSchema } from '../../resource/resource-base/baseSchemas';
import { fetchFn } from '../../resource/resource-base/fetchFn';
import { msObject } from '../../resource/resource-base/msObject';
import type { Auth } from './AuthProvider';
import { loginSchema } from './loginSchema';

const sessionSchema = msObject({
    accessExpiresAt: z.coerce.date(),
    userId: IdSchema,
    userName: z.string().nonempty(),
    email: z.string().email().nullable(),
    name: z.string().nullable(),
    // TODO get rid of useUserActions.useGetResourceByToken
});
type Session = z.infer<typeof sessionSchema>;

const { useLogin, useLogout, useSession } = createAuthentication({
    credentialsSchema: loginSchema,
    sessionSchema: sessionSchema,
    sessionQueryKey: ['session'],
    getAccessTokenExpirationDate: ({ accessExpiresAt }) => accessExpiresAt,
    loginCall: ({ username, password }) =>
        fetchFn(createURL({ origin: API_ROOT, paths: ['auth', 'authenticate'] }), 'POST', undefined, {
            headers: { Authorization: `Basic ${btoa(`${username}:${password}`)}` },
        }),
    logoutCall: () => fetchFn(createURL({ origin: API_ROOT, paths: ['auth', 'logout'] }), 'POST'),
    refreshCall: () => fetchFn<Session>(createURL({ origin: API_ROOT, paths: ['auth', 'refresh'] }), 'POST'),
});

export const SetJwtContext = memo(({ setAuth }: { setAuth: (jwtContext: Auth<false>) => void }) => {
    const { status } = useSession();
    const login = useLogin();
    const logout = useLogout();

    useEffect(() => {
        setAuth(
            when({
                expression: status,
                cases: {
                    loading: { isOpenId: false, authState: 'loading' },
                    authenticated: { isOpenId: false, authState: 'authenticated', logout },
                    unauthenticated: { isOpenId: false, authState: 'unauthenticated', login },
                },
            })
        );
    }, [status, setAuth, login, logout]);

    return null;
});
